/**
 * Token Manager — 管理 accessToken / refreshToken 的存储与自动刷新
 *
 * 职责：
 * 1. 统一存取 accessToken + refreshToken（cookie + localStorage + zustand）
 * 2. 401 时自动调用 /auth/refresh 刷新令牌
 * 3. 并发 401 只触发一次 refresh，其余请求排队等待
 * 4. refresh 失败时清除令牌（由调用方决定跳转）
 */
import axios from 'axios'

import { axiosBaseOptions } from '@/network/axios/axios-setup'

// ---------- 存储 key ----------
const ACCESS_TOKEN_KEY = 'token'
const REFRESH_TOKEN_KEY = 'refreshToken'

// ---------- 内部状态 ----------
let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

// ---------- Cookie 工具 ----------
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? decodeURIComponent(match[2]) : null
}

const setCookie = (name: string, value: string, days = 365): void => {
  const isHttps = import.meta.env.VITE_HTTPS_STATE === 'true'
  const opts = isHttps ? '; path=/; secure; sameSite=strict' : '; path=/'
  document.cookie = `${name}=${value}; expires=${new Date(Date.now() + days * 864e5).toUTCString()}${opts}`
}

const removeCookie = (name: string): void => {
  const isHttps = import.meta.env.VITE_HTTPS_STATE === 'true'
  const opts = isHttps ? '; path=/; secure; sameSite=strict' : '; path=/'
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC${opts}`
}

// ---------- 读取 ----------
export const getAccessToken = (): string => {
  return getCookie(ACCESS_TOKEN_KEY) || localStorage.getItem(ACCESS_TOKEN_KEY) || ''
}

export const getRefreshToken = (): string => {
  return getCookie(REFRESH_TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY) || ''
}

// ---------- 写入 ----------
export const setTokens = (accessToken: string, refreshToken?: string): void => {
  // cookie + localStorage 双写
  setCookie(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)

  if (refreshToken) {
    setCookie(REFRESH_TOKEN_KEY, refreshToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

// ---------- 清除 ----------
export const clearTokens = (): void => {
  removeCookie(ACCESS_TOKEN_KEY)
  removeCookie(REFRESH_TOKEN_KEY)
  removeCookie('user_id')
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

// ---------- 刷新 ----------
/**
 * 尝试刷新 accessToken。
 * 并发调用时只有第一个真正发请求，其余排队等待结果。
 * @returns 新的 accessToken，失败则 reject
 */
export const refreshAccessToken = (): Promise<string> => {
  if (isRefreshing) {
    // 已在刷新中，排队等待
    return new Promise<string>((resolve, reject) => {
      pendingQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true
  const refreshToken = getRefreshToken()

  if (!refreshToken) {
    isRefreshing = false
    return Promise.reject(new Error('No refresh token'))
  }

  // 用独立 axios 实例发请求，避免触发业务拦截器
  return axios
    .post(
      `${axiosBaseOptions.baseURL}/auth/refresh`,
      { refreshToken },
      { timeout: 10000 },
    )
    .then((res) => {
      const data = res.data?.data ?? res.data
      const newAccessToken: string = data?.access_token || data?.accessToken || ''
      const newRefreshToken: string = data?.refresh_token || data?.refreshToken || ''

      if (!newAccessToken) {
        throw new Error('Refresh response missing access_token')
      }

      setTokens(newAccessToken, newRefreshToken || undefined)

      // 通知排队请求
      pendingQueue.forEach(({ resolve }) => resolve(newAccessToken))
      pendingQueue = []

      return newAccessToken
    })
    .catch((err) => {
      // 刷新失败，通知排队请求
      pendingQueue.forEach(({ reject }) => reject(err))
      pendingQueue = []
      clearTokens()
      throw err
    })
    .finally(() => {
      isRefreshing = false
    })
}

/**
 * 页面刷新后把持久化的 token 写回 cookie，避免「看起来已登录但接口 401」
 */
export const syncAuthFromPersistedStore = (): void => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY) || ''
  if (token && !getCookie(ACCESS_TOKEN_KEY)) {
    setCookie(ACCESS_TOKEN_KEY, token)
  }
  const rt = localStorage.getItem(REFRESH_TOKEN_KEY) || ''
  if (rt && !getCookie(REFRESH_TOKEN_KEY)) {
    setCookie(REFRESH_TOKEN_KEY, rt)
  }
}
